import React, { useEffect, useMemo, useRef, useState } from 'react';

const formatCount = (n) => (Number(n) || 0).toLocaleString('ko-KR');

const groupSelectionKey = (groupId) => `group:${groupId}`;
const categorySelectionKey = (categoryId) => `category:${categoryId}`;
const modelSelectionKey = (categoryId, modelName) => `model:${categoryId}::${modelName}`;

const ModelBreakdownPanel = ({
    modelBreakdown,
    categoryTitle,
    categoryId,
    groupLabel,
    selectedSelectionKey,
    onListFilter,
}) => {
    const items = modelBreakdown?.items || [];
    if (items.length === 0) {
        return (
            <p className="as-repair-qty-share-model-detail-empty">
                {categoryTitle}에 해당하는 모델별 수량이 없습니다.
            </p>
        );
    }

    const total = modelBreakdown.totalQuantity || 0;

    return (
        <div className="as-repair-qty-share-model-detail">
            <p className="as-repair-qty-share-model-detail-lead">
                {categoryTitle} — 모델명별 수량 (합계{' '}
                <strong>{formatCount(total)}</strong>
                {' '}기준) · 모델을 클릭하면 해당 A/S 목록만 표시합니다.
            </p>
            <ul className="as-repair-qty-share-model-detail-list">
                {items.map((m) => {
                    const key = modelSelectionKey(categoryId, m.modelName);
                    const isSelected = selectedSelectionKey === key;
                    const hasRows = Array.isArray(m.rowIds) && m.rowIds.length > 0;
                    return (
                        <li
                            key={m.modelName}
                            className={`as-repair-qty-share-model-detail-item${isSelected ? ' as-repair-qty-share-model-detail-item--selected' : ''}${hasRows ? ' as-repair-qty-share-model-detail-item--clickable' : ''}`}
                            role={hasRows ? 'button' : undefined}
                            tabIndex={hasRows ? 0 : undefined}
                            aria-pressed={hasRows ? isSelected : undefined}
                            onClick={hasRows ? (e) => {
                                e.stopPropagation();
                                onListFilter({
                                    selectionKey: key,
                                    rowIds: m.rowIds,
                                    groupLabel,
                                    categoryTitle,
                                    modelName: m.modelName,
                                    rowCount: m.rowCount,
                                });
                            } : undefined}
                            onKeyDown={hasRows ? (e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onListFilter({
                                        selectionKey: key,
                                        rowIds: m.rowIds,
                                        groupLabel,
                                        categoryTitle,
                                        modelName: m.modelName,
                                        rowCount: m.rowCount,
                                    });
                                }
                            } : undefined}
                        >
                            <span className="as-repair-qty-share-model-detail-name">{m.modelName}</span>
                            <span className="as-repair-qty-share-model-detail-qty">
                                {formatCount(m.quantity)}
                                <span className="as-repair-qty-share-model-detail-rows">
                                    {' '}({formatCount(m.rowCount)}건)
                                </span>
                            </span>
                            <span className="as-repair-qty-share-model-detail-pct">{m.percentLabel}</span>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

const SubDetailPanel = ({
    subDetail,
    parentColor,
    parentLabel,
    expandedCategoryId,
    selectedSelectionKey,
    onToggleCategory,
    onCategoryKeyDown,
    onListFilter,
    modelBreakdownByCategoryId,
}) => {
    if (!subDetail?.items?.length) {
        return (
            <p className="as-repair-qty-share-subdetail-lead">하위 항목을 불러오는 중…</p>
        );
    }
    const hasParent = subDetail.parentTotal > 0;
    const openSub = expandedCategoryId
        ? subDetail.items.find((s) => s.id === expandedCategoryId && s.fieldFound)
        : null;
    const openModelBreakdown = openSub
        ? (modelBreakdownByCategoryId?.[openSub.id] || openSub.modelBreakdown)
        : null;
    const modelPanelRef = useRef(null);

    useEffect(() => {
        if (openSub && modelPanelRef.current) {
            modelPanelRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }
    }, [expandedCategoryId, openSub?.id]);

    const applyCategoryFilter = (sub) => {
        if (!sub.fieldFound || !Array.isArray(sub.rowIds) || sub.rowIds.length === 0) {
            return;
        }
        onListFilter({
            selectionKey: categorySelectionKey(sub.id),
            rowIds: sub.rowIds,
            groupLabel: parentLabel || subDetail.parentLabel,
            categoryTitle: sub.title,
            rowCount: sub.rowCount || sub.rowIds.length,
        });
    };

    return (
        <div
            className={`as-repair-qty-share-subdetail${openSub ? ' as-repair-qty-share-subdetail--model-open' : ''}`}
        >
            <p className="as-repair-qty-share-subdetail-lead">
                {subDetail.parentLabel} 합계{' '}
                <strong>{formatCount(subDetail.parentTotal)}</strong>
                {' '}기준 · 항목을 클릭하면 아래 A/S 목록이 해당 항목만 표시됩니다.
            </p>
            <ul className="as-repair-qty-share-subdetail-list">
                {subDetail.items.map((sub) => {
                    const catOpen = expandedCategoryId === sub.id;
                    const canDrill = sub.fieldFound;
                    const catSelected = selectedSelectionKey === categorySelectionKey(sub.id);
                    const hasRows = Array.isArray(sub.rowIds) && sub.rowIds.length > 0;
                    return (
                        <li
                            key={sub.id}
                            className={`as-repair-qty-share-subdetail-item${catOpen ? ' as-repair-qty-share-subdetail-item--open' : ''}${catSelected ? ' as-repair-qty-share-subdetail-item--filter-active' : ''}`}
                        >
                            <div
                                className={`as-repair-qty-share-subdetail-row${canDrill && hasRows ? ' as-repair-qty-share-subdetail-row--clickable' : ''}`}
                                role={canDrill && hasRows ? 'button' : undefined}
                                tabIndex={canDrill && hasRows ? 0 : undefined}
                                aria-expanded={canDrill ? catOpen : undefined}
                                aria-pressed={canDrill && hasRows ? catSelected : undefined}
                                onClick={canDrill && hasRows ? (e) => {
                                    e.stopPropagation();
                                    applyCategoryFilter(sub);
                                    onToggleCategory(sub.id, e);
                                } : undefined}
                                onKeyDown={canDrill && hasRows ? (e) => onCategoryKeyDown(
                                    sub.id,
                                    e,
                                    sub,
                                    parentLabel || subDetail.parentLabel,
                                ) : undefined}
                            >
                                <span className="as-repair-qty-share-subdetail-label">{sub.title}</span>
                                <span className="as-repair-qty-share-subdetail-value">
                                    {formatCount(sub.value)}
                                </span>
                                <span className="as-repair-qty-share-subdetail-pct">
                                    {sub.fieldFound
                                        ? (hasParent ? sub.percentLabel : '-')
                                        : '항목 미설정'}
                                </span>
                                {canDrill ? (
                                    <span className="as-repair-qty-share-subdetail-cat-toggle" aria-hidden>
                                        {catOpen ? '▲' : '▼'}
                                    </span>
                                ) : null}
                                {sub.fieldFound && hasParent ? (
                                    <div className="as-repair-qty-share-subdetail-bar-wrap">
                                        <div
                                            className="as-repair-qty-share-subdetail-bar-fill"
                                            style={{
                                                width: `${Math.min(sub.percentOfParent, 100)}%`,
                                                backgroundColor: parentColor,
                                            }}
                                        />
                                    </div>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {openSub ? (
                <div ref={modelPanelRef} className="as-repair-qty-share-model-detail-wrap">
                    {openModelBreakdown?.items ? (
                        <ModelBreakdownPanel
                            modelBreakdown={openModelBreakdown}
                            categoryTitle={openSub.title}
                            categoryId={openSub.id}
                            groupLabel={parentLabel || subDetail.parentLabel}
                            selectedSelectionKey={selectedSelectionKey}
                            onListFilter={onListFilter}
                        />
                    ) : (
                        <p className="as-repair-qty-share-model-detail-empty">모델별 수량을 불러오는 중…</p>
                    )}
                </div>
            ) : null}
        </div>
    );
};

const AsRepairQtyShareChart = ({
    share,
    rowCount = 0,
    selectedSelectionKey = null,
    onListFilter,
    getSubDetail,
    getCategoryModelBreakdown,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [expandedSubGroupId, setExpandedSubGroupId] = useState(null);
    const [expandedCategoryId, setExpandedCategoryId] = useState(null);
    const [subDetailByGroupId, setSubDetailByGroupId] = useState({});
    const [modelBreakdownByCategoryId, setModelBreakdownByCategoryId] = useState({});

    const shareRevision = useMemo(() => (
        `${rowCount}:${share?.pairTotal ?? 0}:${(share?.inboundBreakdown?.items || []).map((i) => `${i.id}:${i.value}`).join('|')}`
    ), [share, rowCount]);

    useEffect(() => {
        setSubDetailByGroupId({});
        setModelBreakdownByCategoryId({});
        setExpandedSubGroupId(null);
        setExpandedCategoryId(null);
    }, [shareRevision]);

    useEffect(() => {
        if (!expandedSubGroupId || typeof getSubDetail !== 'function') {
            return undefined;
        }
        if (subDetailByGroupId[expandedSubGroupId]) {
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(() => {
            if (cancelled) {
                return;
            }
            const detail = getSubDetail(expandedSubGroupId);
            setSubDetailByGroupId((prev) => (
                prev[expandedSubGroupId] ? prev : { ...prev, [expandedSubGroupId]: detail }
            ));
        }, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [expandedSubGroupId, getSubDetail, subDetailByGroupId]);

    useEffect(() => {
        if (!expandedCategoryId || typeof getCategoryModelBreakdown !== 'function') {
            return undefined;
        }
        if (modelBreakdownByCategoryId[expandedCategoryId]) {
            return undefined;
        }
        const groupDetail = expandedSubGroupId
            ? subDetailByGroupId[expandedSubGroupId]
            : null;
        const subItem = groupDetail?.items?.find((s) => s.id === expandedCategoryId);
        if (!subItem) {
            return undefined;
        }
        let cancelled = false;
        const timer = window.setTimeout(() => {
            if (cancelled) {
                return;
            }
            const breakdown = getCategoryModelBreakdown(subItem);
            setModelBreakdownByCategoryId((prev) => (
                prev[expandedCategoryId] ? prev : { ...prev, [expandedCategoryId]: breakdown }
            ));
        }, 0);
        return () => {
            cancelled = true;
            window.clearTimeout(timer);
        };
    }, [
        expandedCategoryId,
        expandedSubGroupId,
        getCategoryModelBreakdown,
        modelBreakdownByCategoryId,
        subDetailByGroupId,
    ]);

    if (!share || !Array.isArray(share.segments) || share.segments.length === 0) {
        return null;
    }

    const {
        inboundBreakdown,
        receivedSegment,
        visibleSegments = [],
        pairTotal = 0,
        visibleLabel = '진행중+수리완료',
    } = share;
    const hasPairQty = pairTotal > 0;
    const breakdown = inboundBreakdown || { receivedTotal: 0, receivedLabel: '실입고 수량', items: [] };
    const hasReceivedBaseline = breakdown.receivedTotal > 0;
    const detailItems = breakdown.items || [];

    const clearCategory = () => setExpandedCategoryId(null);

    const applyListFilter = (payload) => {
        if (typeof onListFilter === 'function') {
            onListFilter(payload);
        }
    };

    const toggleExpanded = () => {
        setExpanded((prev) => !prev);
        if (expanded) {
            setExpandedSubGroupId(null);
            clearCategory();
        }
    };

    const activateGroup = (item) => {
        if (item.fieldFound && Array.isArray(item.rowIds) && item.rowIds.length > 0) {
            applyListFilter({
                selectionKey: groupSelectionKey(item.id),
                rowIds: item.rowIds,
                groupLabel: item.label,
                rowCount: item.rowCount || item.rowIds.length,
            });
        }
    };

    const toggleSubGroup = (item, e) => {
        e.stopPropagation();
        activateGroup(item);
        setExpandedSubGroupId((prev) => {
            if (prev === item.id) {
                clearCategory();
                return null;
            }
            clearCategory();
            return item.id;
        });
    };

    const toggleCategory = (categoryId, e) => {
        e.stopPropagation();
        setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId));
    };

    const handleCategoryKeyDown = (categoryId, e, sub, groupLabel) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            if (sub?.rowIds?.length) {
                applyListFilter({
                    selectionKey: categorySelectionKey(sub.id),
                    rowIds: sub.rowIds,
                    groupLabel,
                    categoryTitle: sub.title,
                    rowCount: sub.rowCount || sub.rowIds.length,
                });
            }
            setExpandedCategoryId((prev) => (prev === categoryId ? null : categoryId));
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleExpanded();
        }
    };

    const handleSubGroupKeyDown = (item, e) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            toggleSubGroup(item, e);
        }
    };

    return (
        <section
            className={`as-repair-qty-share-chart${expanded ? ' as-repair-qty-share-chart--open' : ''}`}
            aria-label="검색 결과 수량 비중"
        >
            <div
                className="as-repair-qty-share-chart-clickable"
                role="button"
                tabIndex={0}
                aria-expanded={expanded}
                onClick={toggleExpanded}
                onKeyDown={handleKeyDown}
            >
                <div className="as-repair-qty-share-chart-head">
                    <h4 className="as-repair-qty-share-chart-title">수량 비중</h4>
                    <span className="as-repair-qty-share-chart-meta">
                        검색 결과 {rowCount}건 · {visibleLabel} {formatCount(pairTotal)}
                    </span>
                    <span className="as-repair-qty-share-chart-toggle" aria-hidden>
                        {expanded ? '세부사항 닫기 ▲' : '세부사항 열기 ▼'}
                    </span>
                </div>

                <div
                    className={`as-repair-qty-share-bar${hasPairQty ? '' : ' as-repair-qty-share-bar--empty'}`}
                    role="img"
                    aria-label={
                        hasPairQty
                            ? visibleSegments.map((s) => `${s.label} ${s.percent}%`).join(', ')
                            : '표시할 수량 없음'
                    }
                >
                    {hasPairQty ? (
                        <>
                            {receivedSegment ? (
                                <div
                                    className="as-repair-qty-share-bar-backdrop"
                                    style={{ backgroundColor: receivedSegment.color }}
                                    title={`${receivedSegment.label}: ${formatCount(receivedSegment.value)} (배경)`}
                                    aria-hidden
                                />
                            ) : null}
                            <div className="as-repair-qty-share-bar-foreground">
                                {visibleSegments.map((seg) => (
                                    seg.percent > 0 ? (
                                        <div
                                            key={seg.id}
                                            className="as-repair-qty-share-bar-segment"
                                            style={{
                                                width: `${seg.percent}%`,
                                                backgroundColor: seg.color,
                                            }}
                                            title={`${seg.label}: ${formatCount(seg.value)} (${seg.percent}%)`}
                                        />
                                    ) : null
                                ))}
                            </div>
                        </>
                    ) : (
                        <div className="as-repair-qty-share-bar-placeholder">표시할 수량 없음</div>
                    )}
                </div>

                <ul className="as-repair-qty-share-legend as-repair-qty-share-legend--pair">
                    {visibleSegments.map((seg) => (
                        <li key={seg.id} className="as-repair-qty-share-legend-item">
                            <span
                                className="as-repair-qty-share-legend-swatch"
                                style={{ backgroundColor: seg.color }}
                                aria-hidden
                            />
                            <span className="as-repair-qty-share-legend-label">{seg.label}</span>
                            <span className="as-repair-qty-share-legend-value">
                                {formatCount(seg.value)}
                            </span>
                            <span className="as-repair-qty-share-legend-percent">
                                {hasPairQty ? `${seg.percent}%` : '-'}
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {expanded ? (
                <div
                    className="as-repair-qty-share-detail"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    role="presentation"
                >
                    <p className="as-repair-qty-share-detail-lead">
                        검색 결과 {rowCount}건 합산 · {breakdown.receivedLabel}{' '}
                        <strong>{formatCount(breakdown.receivedTotal)}</strong>
                        {' '}기준 · 상위/하위 항목을 클릭하면 아래 A/S 목록이 해당 건만 표시됩니다.
                    </p>

                    <ul className="as-repair-qty-share-detail-list">
                        {detailItems.map((item) => {
                            const subOpen = expandedSubGroupId === item.id;
                            const groupSelected = selectedSelectionKey === groupSelectionKey(item.id);
                            const hasRows = Array.isArray(item.rowIds) && item.rowIds.length > 0;
                            const resolvedSubDetail = item.subDetail || subDetailByGroupId[item.id];
                            return (
                                <li
                                    key={item.id}
                                    className={`as-repair-qty-share-detail-item${subOpen ? ' as-repair-qty-share-detail-item--sub-open' : ''}${groupSelected ? ' as-repair-qty-share-detail-item--filter-active' : ''}`}
                                >
                                    <div
                                        className={`as-repair-qty-share-detail-item-clickable${item.fieldFound && hasRows ? '' : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        aria-expanded={subOpen}
                                        aria-pressed={item.fieldFound && hasRows ? groupSelected : undefined}
                                        onClick={(e) => toggleSubGroup(item, e)}
                                        onKeyDown={(e) => handleSubGroupKeyDown(item, e)}
                                    >
                                        <div className="as-repair-qty-share-detail-item-head">
                                            <span
                                                className="as-repair-qty-share-detail-swatch"
                                                style={{ backgroundColor: item.color }}
                                                aria-hidden
                                            />
                                            <span className="as-repair-qty-share-detail-label">{item.label}</span>
                                            <span className="as-repair-qty-share-detail-value">
                                                {formatCount(item.value)}
                                            </span>
                                            <span className="as-repair-qty-share-detail-percent">
                                                {item.fieldFound
                                                    ? (hasReceivedBaseline ? item.percentLabel : '-')
                                                    : '항목 미설정'}
                                            </span>
                                            <span className="as-repair-qty-share-detail-sub-toggle" aria-hidden>
                                                {subOpen ? '▲' : '▼'}
                                            </span>
                                        </div>
                                        {item.fieldFound && hasReceivedBaseline ? (
                                            <div className="as-repair-qty-share-detail-bar-wrap">
                                                <div
                                                    className="as-repair-qty-share-detail-bar-fill"
                                                    style={{
                                                        width: `${Math.min(item.percentOfReceived, 100)}%`,
                                                        backgroundColor: item.color,
                                                    }}
                                                />
                                            </div>
                                        ) : null}
                                    </div>
                                    {subOpen ? (
                                        <SubDetailPanel
                                            subDetail={resolvedSubDetail}
                                            parentColor={item.color}
                                            parentLabel={item.label}
                                            expandedCategoryId={expandedCategoryId}
                                            selectedSelectionKey={selectedSelectionKey}
                                            onToggleCategory={toggleCategory}
                                            onCategoryKeyDown={handleCategoryKeyDown}
                                            onListFilter={applyListFilter}
                                            modelBreakdownByCategoryId={modelBreakdownByCategoryId}
                                        />
                                    ) : null}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ) : null}
        </section>
    );
};

export default AsRepairQtyShareChart;
